import classNames from 'classnames';
import moment from 'moment';
import PropTypes from 'prop-types';
import { useEffect, useRef, useState } from 'react';

import atlanticBadge from '@/assets/images/atlantic-health-mini.png';
import placeholder from '@/assets/images/Doctor_Placeholder@2x.png';
import { Breadcrumb } from '@/design-system/atoms/Breadcrumb';
import { Button } from '@/design-system/atoms/Button';
import { ContentContainer } from '@/design-system/atoms/ContentContainer';
import { Grid, GridColumn } from '@/design-system/atoms/Grid';
import { LazyIconCalendar } from '@/design-system/atoms/Icons/IconCalendar/Lazy';
import { LazyIconCheck } from '@/design-system/atoms/Icons/IconCheck/Lazy';
import { LazyIconPhone } from '@/design-system/atoms/Icons/IconPhone/Lazy';
import { Image } from '@/design-system/atoms/Image';
import { StarRatings } from '@/design-system/atoms/StarRatings';
import { Text } from '@/design-system/atoms/Text';
import { Theme } from '@/design-system/atoms/Theme';
import { Title } from '@/design-system/atoms/Title';
import { useBreakpoint } from '@/hooks/useBreakpoint';
import { usePhysicianBioAvailabilityDate } from '@/hooks/usePhysicianBioAvailabilityDate';

import styles from './PhysicianBioHero.module.scss';

const PhysicianBioHero = ({
    id,
    breadcrumbs,
    providerImage,
    acceptingNewPatients,
    onlineBooking,
    additionalVisitTypes,
    providerName,
    primarySpecialty,
    specialties,
    averageRating,
    numberOfReviews,
    reviewsLink = '#reviews',
    affiliationBadgeLabel,
    languages,
    gender,
    lgbtServices,
    insurancesText = 'Want to know your insurance options?',
    insurancesLinkText = 'See Full List',
    insurancesAriaLabel = 'See full list of Insurances',
    insuranceLink,
    bookAppointmentText = 'Book Appointment',
    bookAppointmentAriaLabel = 'Book an appointment with this provider',
    availabilityText = 'Available as soon as: ',
    npi,
    newPatientAppointmentIds,
    departmentIds,
    appointmentUrl,
    phoneNumber,
    ...props
}) => {
    const physicianBioHeightRef = useRef();
    const physicianBioBgRef = useRef();
    const physicianBioContainerRef = useRef();

    const [bgHeight, setBgHeight] = useState(0);
    const [isVisible, setIsVisible] = useState(false);
    const [isReady, setIsReady] = useState(false);

    const { availabilityDate } = usePhysicianBioAvailabilityDate({
        npi,
        newPatientAppointmentIds,
        departmentIds,
    });
    const breakpoints = useBreakpoint();
    const isMobile = !breakpoints?.medium;

    const ImageComponent = providerImage ? (
        <Image {...providerImage} fallbackSrc={placeholder} alt={providerName} />
    ) : (
        <Image src={placeholder} alt="Doctor" />
    );

    const filteredLanguages = languages?.filter((language) => language !== 'English');

    let renderedSpecialties = [];
    let filteredSpecialties = specialties;

    if (primarySpecialty) {
        renderedSpecialties.push(primarySpecialty);
        if (Array.isArray(specialties)) {
            filteredSpecialties = specialties.filter((specialty) => specialty !== primarySpecialty);
        }
    }
    const otherSpecialties = filteredSpecialties?.slice(0, primarySpecialty ? 2 : 3);
    const restSpecialties = filteredSpecialties?.slice(primarySpecialty ? 2 : 3);

    if (otherSpecialties?.length > 0) {
        for (var i = 0; i < otherSpecialties.length; i++) {
            renderedSpecialties.push(otherSpecialties[i]);
        }
    }
    const remainderSpecialities =
        restSpecialties?.length > 0 ? `+ ${restSpecialties.length} more` : null;

    const analyticsSpecialties =
        renderedSpecialties?.length > 0
            ? `${renderedSpecialties.join(', ')}${remainderSpecialities || ''}`
            : '';

    const ScrollHandler = () => {
        if (window.scrollY >= physicianBioHeightRef?.current.clientHeight) {
            setIsVisible(true);
        } else {
            setIsVisible(false);
        }
    };

    useEffect(() => {
        const containerHeight = physicianBioHeightRef?.current.clientHeight;
        const paddingAmount = parseInt(
            window
                .getComputedStyle(physicianBioContainerRef?.current)
                .getPropertyValue('padding-top'),
        );
        const totalHeight = containerHeight / 2 + paddingAmount;
        setBgHeight(totalHeight);
    }, []);

    useEffect(() => {
        window.addEventListener('scroll', ScrollHandler);
        return () => {
            window.removeEventListener('scroll', ScrollHandler);
        };
    }, []);

    useEffect(() => {
        setIsReady(true);
    }, []);

    return (
        <>
            <section
                className={styles[`physician-bio-hero`]}
                ref={physicianBioHeightRef}
                id={id}
                {...props}
            >
                <div
                    className={styles[`physician-bio-hero__bg`]}
                    ref={physicianBioBgRef}
                    style={{ '--height': `${bgHeight}px` }}
                >
                    <Theme
                        name={Theme.NAME.PROVIDERS}
                        className={styles[`physician-bio-hero__theme`]}
                    />
                </div>

                <ContentContainer
                    className={styles[`physician-bio-hero__container`]}
                    ref={physicianBioContainerRef}
                >
                    {breadcrumbs && (
                        <Breadcrumb
                            items={breadcrumbs}
                            className={styles[`physician-bio-hero__breadcrumb`]}
                        />
                    )}
                    <Grid
                        col={{ sm: 4, md: 6, lg: 12 }}
                        row={{ sm: 3, md: 3, lg: 2 }}
                        className={styles[`physician-bio-hero__grid`]}
                    >
                        <GridColumn
                            colSpan={{ sm: 2, md: 2, lg: 3 }}
                            colStart={{ sm: 1, md: 1, lg: 1 }}
                            rowStart={{ sm: 1, md: 1, lg: 1 }}
                            rowSpan={{ sm: 1, md: 2, lg: 2 }}
                            className={styles[`physician-bio-hero__image`]}
                        >
                            {ImageComponent}
                        </GridColumn>
                        <GridColumn
                            colSpan={{ sm: 2, md: 4, lg: 5 }}
                            colStart={{ sm: 3, md: 3, lg: 4 }}
                            rowSpan={{ sm: 1, md: 1, lg: 1 }}
                            rowStart={{ sm: 1, md: 1, lg: 1 }}
                            className={styles[`physician-bio-hero__patient-info`]}
                        >
                            {acceptingNewPatients && (
                                <Text
                                    content="Accepting New Patients"
                                    tag={Text.TAG.P}
                                    variant={Text.VARIANT.T4}
                                    className={styles[`physician-bio-hero__accepting-badge`]}
                                />
                            )}
                            <div className={styles[`physician-bio-hero__visit-container`]}>
                                {onlineBooking && (
                                    <div className={styles[`physician-bio-hero__visit`]}>
                                        <LazyIconCheck />
                                        <Text
                                            content="Online Booking"
                                            tag={Text.TAG.P}
                                            variant={Text.VARIANT.T3}
                                            className={styles[`physician-bio-hero__visit-type`]}
                                        />
                                    </div>
                                )}
                                {additionalVisitTypes &&
                                    Array.isArray(additionalVisitTypes) &&
                                    additionalVisitTypes.map((type, index) => (
                                        <div
                                            className={styles[`physician-bio-hero__visit`]}
                                            key={index}
                                        >
                                            <LazyIconCheck />
                                            <Text
                                                content={type}
                                                tag={Text.TAG.P}
                                                variant={Text.VARIANT.T3}
                                                className={styles[`physician-bio-hero__visit-type`]}
                                            />
                                        </div>
                                    ))}
                            </div>
                        </GridColumn>
                        <GridColumn
                            colSpan={{ sm: 4, md: 4, lg: 5 }}
                            colStart={{ sm: 1, md: 3, lg: 4 }}
                            rowSpan={{ sm: 1, md: 1, lg: 1 }}
                            rowStart={{ sm: 2, md: 2, lg: 2 }}
                            className={styles[`physician-bio-hero__info`]}
                        >
                            <Title
                                content={providerName}
                                tag={Title.TAG.H1}
                                variant={Title.VARIANT.H3}
                                className={styles[`physician-bio-hero__title`]}
                            />
                            {renderedSpecialties?.length > 0 && (
                                <div
                                    className={styles[`physician-bio-hero__specialties-container`]}
                                >
                                    <Text
                                        content={renderedSpecialties.join(', ')}
                                        tag={Text.TAG.P}
                                        variant={Text.VARIANT.T2}
                                        className={styles[`physician-bio-hero__specialties`]}
                                    />
                                    {remainderSpecialities && (
                                        <Button
                                            label={remainderSpecialities}
                                            buttonStyle={Button.STYLE.TEXT_SMALL}
                                            className={
                                                styles[`physician-bio-hero__specialties-more`]
                                            }
                                            href="#about"
                                        />
                                    )}
                                </div>
                            )}
                            <div className={styles[`physician-bio-hero__footer`]}>
                                {typeof averageRating === 'number' && averageRating > 0 && (
                                    <div className={styles[`physician-bio-hero__rating-container`]}>
                                        <StarRatings ratings={averageRating} total={5} />
                                        <Text
                                            content={averageRating?.toFixed(1)}
                                            tag={Text.TAG.SPAN}
                                            variant={Text.VARIANT.T3}
                                            className={styles[`physician-bio-hero__rating-text`]}
                                        />
                                        <div
                                            className={
                                                styles[`physician-bio-hero__reviews-container`]
                                            }
                                        >
                                            <Text
                                                content={`(`}
                                                tag={Text.TAG.SPAN}
                                                variant={Text.VARIANT.T3}
                                            />
                                            <Button
                                                buttonStyle={Button.STYLE.TEXT_SMALL}
                                                label={numberOfReviews}
                                                href={reviewsLink}
                                                className={styles[`physician-bio-hero__reviews`]}
                                                analytics={{
                                                    componentName: 'fad_bio_hero',
                                                    actionLabel: 'reviews',
                                                    acceptingNewPatients: acceptingNewPatients,
                                                    providerName: providerName,
                                                    providerSpecialties: analyticsSpecialties,
                                                    providerRating: averageRating,
                                                    interactionType: 'fad_bio_cta',
                                                }}
                                            />
                                            <Text
                                                content={`)`}
                                                tag={Text.TAG.SPAN}
                                                variant={Text.VARIANT.T3}
                                            />
                                        </div>
                                    </div>
                                )}
                                {affiliationBadgeLabel !== '' && (
                                    <div className={styles[`physician-bio-hero__affiliation`]}>
                                        <Image
                                            src={atlanticBadge}
                                            alt="Affiliation"
                                            className={
                                                styles[`physician-bio-hero__affiliation-image`]
                                            }
                                        />
                                        <Text
                                            content={affiliationBadgeLabel}
                                            tag={Text.TAG.P}
                                            variant={Text.VARIANT.T3}
                                        />
                                    </div>
                                )}
                            </div>
                            <div className={styles[`physician-bio-hero__flex-container`]}>
                                {Array.isArray(filteredLanguages) &&
                                    filteredLanguages.length > 0 && (
                                        <div className={styles[`physician-bio-hero__pill`]}>
                                            <Text
                                                content={`Speaks: ${filteredLanguages.join(', ')}`}
                                                tag={Text.TAG.P}
                                                variant={Text.VARIANT.T3}
                                            />
                                        </div>
                                    )}
                                {gender && (
                                    <div className={styles[`physician-bio-hero__pill`]}>
                                        <Text
                                            content={gender}
                                            tag={Text.TAG.P}
                                            variant={Text.VARIANT.T3}
                                        />
                                    </div>
                                )}
                                {lgbtServices && (
                                    <div className={styles[`physician-bio-hero__pill`]}>
                                        <Text
                                            content="LGBTQ+ Health"
                                            tag={Text.TAG.P}
                                            variant={Text.VARIANT.T3}
                                        />
                                    </div>
                                )}
                            </div>
                            {insuranceLink && (
                                <div className={styles[`physician-bio-hero__insurance-container`]}>
                                    <Text
                                        content={insurancesText}
                                        tag={Text.TAG.P}
                                        variant={Text.VARIANT.T3}
                                        className={styles[`physician-bio-hero__insurance-text`]}
                                    />
                                    <Button
                                        label={insurancesLinkText}
                                        ariaLabel={insurancesAriaLabel}
                                        href={insuranceLink}
                                        buttonStyle={Button.STYLE.TEXT_SMALL}
                                        className={styles[`physician-bio-hero__insurance-button`]}
                                        analytics={{
                                            componentName: 'fad_bio_hero',
                                            actionLabel: insurancesLinkText,
                                            acceptingNewPatients: acceptingNewPatients,
                                            providerName: providerName,
                                            providerSpecialties: analyticsSpecialties,
                                            providerRating: averageRating,
                                            interactionType: 'fad_bio_cta',
                                        }}
                                    />
                                </div>
                            )}
                        </GridColumn>
                        <GridColumn
                            colSpan={{ sm: 4, md: 6, lg: 4 }}
                            colStart={{ sm: 1, md: 1, lg: 9 }}
                            rowSpan={{ sm: 1, md: 1, lg: 2 }}
                            rowStart={{ sm: 3, md: 3, lg: 1 }}
                            className={styles[`physician-bio-hero__appointment-block`]}
                        >
                            <Theme
                                name={Theme.NAME.PROVIDERS}
                                className={styles[`physician-bio-hero__appointment-block--inner`]}
                            >
                                {onlineBooking && availabilityDate && (
                                    <p className={styles[`physician-bio-hero__availability-text`]}>
                                        <Text
                                            content={`${availabilityText} `}
                                            tag={Text.TAG.SPAN}
                                            variant={Text.VARIANT.T2}
                                        />
                                        <Text
                                            content={`${moment(availabilityDate).format(
                                                'ddd MMM DD',
                                            )}`}
                                            tag={Text.TAG.SPAN}
                                            variant={Text.VARIANT.T2B}
                                        />
                                    </p>
                                )}
                                {onlineBooking && appointmentUrl && (
                                    <Button
                                        label={bookAppointmentText}
                                        ariaLabel={bookAppointmentAriaLabel}
                                        href={appointmentUrl}
                                        buttonStyle={Button.STYLE.PRIMARY}
                                        iconComponent={LazyIconCalendar}
                                        className={styles[`physician-bio-hero__full-width-button`]}
                                        analytics={{
                                            componentName: 'fad_bio_hero',
                                            actionLabel: bookAppointmentText,
                                            acceptingNewPatients: acceptingNewPatients,
                                            providerName: providerName,
                                            providerSpecialties: analyticsSpecialties,
                                            providerRating: averageRating,
                                            interactionType: 'fad_bio_primary_cta',
                                        }}
                                    />
                                )}
                                {phoneNumber && (
                                    <Button
                                        label={`Call ${phoneNumber}`}
                                        ariaLabel={`Call ${phoneNumber}`}
                                        href={`tel:${phoneNumber}`}
                                        buttonStyle={
                                            onlineBooking && appointmentUrl
                                                ? Button.STYLE.SECONDARY
                                                : Button.STYLE.PRIMARY
                                        }
                                        iconComponent={LazyIconPhone}
                                        className={styles[`physician-bio-hero__full-width-button`]}
                                        analytics={{
                                            componentName: 'fad_bio_hero',
                                            actionLabel: `Call-${phoneNumber}`,
                                            acceptingNewPatients: acceptingNewPatients,
                                            providerName: providerName,
                                            providerSpecialties: analyticsSpecialties,
                                            providerRating: averageRating,
                                            interactionType: 'fad_bio_cta',
                                        }}
                                    />
                                )}
                            </Theme>
                        </GridColumn>
                    </Grid>
                </ContentContainer>
            </section>
            <ContentContainer
                className={classNames(
                    styles[`physician-bio-hero__sticky`],
                    isVisible && styles[`physician-bio-hero__sticky--visible`],
                )}
            >
                <Theme
                    name={Theme.NAME.PROVIDERS}
                    className={styles[`physician-bio-hero__sticky-container`]}
                >
                    <div className={styles[`physician-bio-hero__availability`]}>
                        <Title
                            variant={Title.VARIANT.T1B}
                            tag={Title.TAG.H2}
                            content={providerName}
                        />
                        {onlineBooking && availabilityDate && (
                            <Text
                                variant={Text.VARIANT.T3}
                                tag={Text.TAG.P}
                                content={`Available for appointments starting ${moment(
                                    availabilityDate,
                                ).format('ddd MMM DD')}`}
                            />
                        )}
                    </div>
                    {onlineBooking && appointmentUrl ? (
                        <div>
                            <Button
                                label={isMobile || !isReady ? undefined : bookAppointmentText}
                                ariaLabel={bookAppointmentAriaLabel}
                                href={appointmentUrl}
                                buttonStyle={Button.STYLE.PRIMARY}
                                iconComponent={LazyIconCalendar}
                                className={styles[`physician-bio-hero__banner-button`]}
                                analytics={{
                                    componentName: 'fad_bio_hero',
                                    actionLabel: bookAppointmentText,
                                    acceptingNewPatients: acceptingNewPatients,
                                    providerName: providerName,
                                    providerSpecialties: analyticsSpecialties,
                                    providerRating: averageRating,
                                    interactionType: 'fad_bio_primary_cta',
                                }}
                                tabIndex={isVisible ? undefined : -1}
                            />
                        </div>
                    ) : (
                        <div>
                            <Button
                                label={isMobile || !isReady ? undefined : `Call ${phoneNumber}`}
                                ariaLabel={`Call ${phoneNumber}`}
                                href={`tel:${phoneNumber}`}
                                buttonStyle={Button.STYLE.PRIMARY}
                                iconComponent={LazyIconPhone}
                                className={styles[`physician-bio-hero__banner-button`]}
                                analytics={{
                                    componentName: 'fad_bio_hero',
                                    actionLabel: `Call-${phoneNumber}`,
                                    acceptingNewPatients: acceptingNewPatients,
                                    providerName: providerName,
                                    providerSpecialties: analyticsSpecialties,
                                    providerRating: averageRating,
                                    interactionType: 'fad_bio_cta',
                                }}
                                tabIndex={isVisible ? undefined : -1}
                            />
                        </div>
                    )}
                </Theme>
            </ContentContainer>
        </>
    );
};

PhysicianBioHero.propTypes = {
    /**
     * The image of the provider
     */
    providerImage: PropTypes.shape(Image.propTypes),
    /**
     * Whether the provider is accepting new patients
     */
    acceptingNewPatients: PropTypes.bool,
    /**
     * Whether the provider offers online booking
     * */
    onlineBooking: PropTypes.bool,
    /**
     * The visit type of the provider
     */
    visitType: PropTypes.string,
    /**
     * The name of the provider
     */
    providerName: PropTypes.string,
    /**
     * The specialities of the provider
     */
    specialities: PropTypes.arrayOf(PropTypes.string),
    /**
     * The average rating of the provider
     */
    averageRating: PropTypes.number,
    /**
     * The number of reviews for the provider
     */
    numberOfReviews: PropTypes.number,
    /**
     * whether a provider has an Affiliation label = Atlantic Medical Group
     */
    affiliationBadgeLabel: PropTypes.string,
    /**
     * The languages spoken by the provider
     */
    languages: PropTypes.arrayOf(PropTypes.string),
    /**
     * Gender of the Provider
     */
    gender: PropTypes.string,
    /**
     * Whether the provider offers LGBT services
     */
    lgbtServices: PropTypes.bool,
    /**
     * Link to the insurance page
     */
    insuranceLink: PropTypes.string,
    /**
     * provider id
     */
    npi: PropTypes.string,
    /**
     * visit type array
     */
    newPatientAppointmentIds: PropTypes.arrayOf(PropTypes.string),
    /**
     * The appointment URL
     */
    appointmentUrl: PropTypes.string,
    /**
     * The phone number of the provider
     */
    phoneNumber: PropTypes.string,
};

export default PhysicianBioHero;
